import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { Box, Stack, type SxProps, type Theme, useMediaQuery } from '@mui/material';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { _ } from '/@/locale';
import Icon from '../icon';

const getOverlayConfigs = () => [
    {
        icon: 'corn',
        label: _('Maíz'),
        color: 'orange',
        type: 'corn',
        children: [
            { label: _('Enfermedades'), type: 'corn_diseases_historic_pressure' },
            { label: _('Plagas'), type: 'corn_pests_historic_pressure' },
            { label: _('Malezas'), type: 'weeds_rem' },
            { label: _('Caracteres Agrónomicos'), type: 'corn_others' },
        ],
        id: 'corn',
    },
    {
        icon: 'soy',
        label: _('Soja'),
        color: 'green',
        type: 'soy',
        children: [{ label: _('Malezas'), type: 'weeds_rem' }],
        id: 'soy',
    },
];

const cardSx: SxProps<Theme> = {
    background: 'white',
    borderRadius: '10px',
    boxShadow: '-20px 10px 20px 0px rgba(0, 0, 0, 0.15)',
    display: 'flex',
    alignItems: 'center',
    margin: '12px',
    position: 'relative',
};

type Overlay = { label: string; id: string; type: string; children?: Overlay[] };

const IncidenceMapsOverlay = ({
    maps,
    setSelectedOverlay,
}: {
    maps: any[];
    setSelectedOverlay: (overlay: any) => void;
}) => {
    const [activeCropIndex, setActiveCropIndex] = useState<number>(0);
    const [activeTypeIndex, setActiveTypeIndex] = useState<number>(0);
    const [activeMapIndex, setActiveMapIndex] = useState<number | null>(null);

    const [cropAnchorEl, setCropAnchorEl] = useState<null | HTMLElement>(null);
    const [typeAnchorEl, setTypeAnchorEl] = useState<null | HTMLElement>(null);
    const [mapAnchorEl, setMapAnchorEl] = useState<null | HTMLElement>(null);

    const cropOverlays = useMemo<Overlay[]>(() => {
        return getOverlayConfigs()
            .map((cropOverlay) => ({
                ...cropOverlay,
                children: cropOverlay.children
                    .map((typeOverlay) => ({
                        ...typeOverlay,
                        children: maps
                            .filter(({ type }) => type === typeOverlay.type)
                            .map((map) => ({
                                ...map,
                                label: _(map.name),
                                id: map.id,
                            })),
                    }))
                    .filter((typeOverlay) => typeOverlay.children.length > 0),
            }))
            .filter((cropOverlay) => cropOverlay.children.length > 0);
    }, [maps]);

    const isMobile = useMediaQuery((theme) => theme.breakpoints.down('sm'));

    const activeCrop = cropOverlays[activeCropIndex];
    const activeType = cropOverlays[activeCropIndex]?.children?.[activeTypeIndex];
    const activeMap =
        activeMapIndex !== null
            ? cropOverlays[activeCropIndex]?.children?.[activeTypeIndex]?.children?.[activeMapIndex]
            : null;

    useEffect(() => {
        if (activeMap !== null)
            setSelectedOverlay({
                overlay: activeMap,
                version: activeMap?.versions?.[0],
            });
        else setSelectedOverlay(undefined);
    }, [activeMap]);

    const handleMenuClick = useCallback(
        (setter: React.Dispatch<React.SetStateAction<HTMLElement | null>>) =>
            (event: React.MouseEvent<HTMLElement>) => {
                setter(event.currentTarget);
            },
        []
    );

    const handleMenuClose = useCallback(
        (setter: React.Dispatch<React.SetStateAction<HTMLElement | null>>) => () => {
            setter(null);
        },
        []
    );

    const renderMenuItems = useCallback((items: Overlay[], onClick: (index: number) => void) => {
        return items.map(({ label }, index) => (
            <Box key={index} sx={{ height: '100%' }}>
                <MenuItem onClick={() => onClick(index)}>{label}</MenuItem>
            </Box>
        ));
    }, []);

    const MenuBox = useCallback(
        ({ label, onClick }: { label?: string; onClick: (event: React.MouseEvent<HTMLElement>) => void }) => (
            <Box
                sx={{ p: { xs: 0.5, md: 3 }, cursor: 'pointer', height: '100%', fontSize: { xs: 14, sm: 14 } }}
                onClick={onClick}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                    {label}
                    <KeyboardArrowDownIcon />
                </Box>
            </Box>
        ),
        []
    );

    return (
        <Stack
            direction='row'
            sx={[
                cardSx,
                { width: { sm: 'fit-content' }, zIndex: 1 },
                isMobile && {
                    m: 0,
                    border: 'none',
                    borderRadius: 0,
                    position: 'fixed',
                    bottom: 0,
                    textAlign: 'center',
                    height: '10%',
                    borderTop: '1px lightgray solid',
                    width: '100%',
                },
            ]}
        >
            {!isMobile && (
                <Box sx={{ p: 1, borderRight: '1px lightgray solid' }}>
                    <Icon type='diseases' size='md' />
                </Box>
            )}

            {/* Crop */}
            <MenuBox label={activeCrop?.label} onClick={handleMenuClick(setCropAnchorEl)} />
            <Menu
                id='crop-menu'
                anchorEl={cropAnchorEl}
                open={Boolean(cropAnchorEl)}
                onClose={handleMenuClose(setCropAnchorEl)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            >
                {renderMenuItems(cropOverlays, (index) => {
                    setCropAnchorEl(null);
                    setActiveCropIndex(index);
                    setActiveTypeIndex(0);
                    setActiveMapIndex(null);
                })}
            </Menu>

            {/* Type */}
            <MenuBox label={activeType?.label} onClick={handleMenuClick(setTypeAnchorEl)} />
            <Menu
                id='type-menu'
                anchorEl={typeAnchorEl}
                open={Boolean(typeAnchorEl)}
                onClose={handleMenuClose(setTypeAnchorEl)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            >
                {renderMenuItems(activeCrop?.children || [], (index) => {
                    setTypeAnchorEl(null);
                    setActiveTypeIndex(index);
                    setActiveMapIndex(null);
                })}
            </Menu>

            {/* Map */}
            <MenuBox label={activeMap?.label || _('Nombre')} onClick={handleMenuClick(setMapAnchorEl)} />
            <Menu
                id='map-menu'
                anchorEl={mapAnchorEl}
                open={Boolean(mapAnchorEl)}
                onClose={handleMenuClose(setMapAnchorEl)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            >
                {renderMenuItems(activeType?.children || [], (index) => {
                    setMapAnchorEl(null);
                    setActiveMapIndex(index);
                })}
            </Menu>
        </Stack>
    );
};

export default IncidenceMapsOverlay;
